import * as React from "react";
import { useNavigate } from "react-router";

/** Which shortcut surface is open. Search is Cmd+K and Zot is Cmd+J. */
export type CommandSurface = "search" | "zot" | null;

/**
 * Destinations reachable with the `G` prefix, as section 4 of the design system lists them.
 * A letter with no entry does nothing rather than guessing.
 */
const gotoDestinations: Record<string, { path: string; label: string }> = {
  a: { path: "/approvals", label: "Approvals" },
  r: { path: "/runs", label: "Runs" },
};

/** How long the shell waits for the second letter of a `G` sequence. */
const GOTO_WINDOW_MS = 1000;

/** Text entry, dialogs, and menus keep their keys; the same exclusions the `N` shortcut uses. */
function isTypingContext(target: EventTarget | null): boolean {
  const element = target as HTMLElement | null;
  if (!element?.closest) return false;
  return Boolean(
    element.closest(
      "input, textarea, select, [contenteditable], [role='dialog'], [role='listbox'], [role='menu']",
    ),
  );
}

/**
 * Listens for the shell shortcuts: Cmd+K search, Cmd+J Zot, and the `G` sequences.
 * The sequence is the only one with state, so it carries a short window and is abandoned
 * on the next keypress if that key is not a destination.
 */
export function useCommandSurface(): [CommandSurface, (surface: CommandSurface) => void] {
  const [surface, setSurface] = React.useState<CommandSurface>(null);
  const navigate = useNavigate();

  React.useEffect(() => {
    let gotoArmedUntil = 0;

    const onKey = (event: KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey || event.altKey) {
        if (!(event.metaKey || event.ctrlKey) || event.altKey || event.shiftKey) return;
        if (event.key.toLowerCase() === "k") {
          event.preventDefault();
          setSurface("search");
        } else if (event.key.toLowerCase() === "j") {
          event.preventDefault();
          setSurface("zot");
        }
        return;
      }

      if (isTypingContext(event.target) || event.key.length !== 1) return;

      const key = event.key.toLowerCase();
      const now = Date.now();

      if (gotoArmedUntil > now) {
        gotoArmedUntil = 0;
        const destination = gotoDestinations[key];
        if (destination) {
          event.preventDefault();
          navigate(destination.path);
        }
        return;
      }

      if (key === "g") {
        gotoArmedUntil = now + GOTO_WINDOW_MS;
      }
    };

    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [navigate]);

  return [surface, setSurface];
}
